import { List, ListItem } from "@solomon-apps/shared/src/components/Data/List";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { useIsMobileViewport } from "@solomon-apps/shared/src/hooks/useIsMobileViewport";
import { ArrowShortUpRightIcon } from "@solomon-labs/design/components/icons";
import { Address, type AddressTier } from "@solomon-labs/design/components/ui";
import React, { useMemo } from "react";

import { useAllClientDistributions } from "@/hooks/useClientPositions";
import { useSavedDestinations } from "@/hooks/useClientSavedDestinations";
import type { Holding } from "@/pages/Positions/holdings";
import type { DistributionItem } from "@/services/clientPositions";

import { formatLongDate, formatUsd, solscanTxUrl } from "../../shared/format";

interface PositionTransactionsCardProps {
    holding: Holding;
    /** Show the "Transactions" heading. Off under the position page's tabs,
     *  where the tab already names it. */
    showTitle?: boolean;
}

/**
 * A holding's distributions, combined across its underlying positions, newest
 * first and de-duplicated by distribution uuid. A payout carrying a
 * `positionUuid` belongs to the holding when that uuid is one of its
 * positions. A legacy address payout also needs a matching source wallet and
 * surface. LP payouts need a position ID because one wallet can own several LPs.
 */
export function holdingDistributions(
    distributions: readonly DistributionItem[],
    holding: Pick<Holding, "positions" | "kind">
): DistributionItem[] {
    const uuids = new Set(holding.positions.map((p) => p.uuid));
    const seen = new Set<string>();
    return distributions
        .filter((d) => {
            const mine = d.positionUuid
                ? uuids.has(d.positionUuid)
                : holding.kind === "address" &&
                  holding.positions.some(
                      (p) =>
                          p.account.address === d.sourceWallet &&
                          p.programSurface.name === d.pool
                  );
            if (!mine || seen.has(d.uuid)) return false;
            seen.add(d.uuid);
            return true;
        })
        .sort(
            (a, b) =>
                new Date(b.distributionDate).getTime() -
                new Date(a.distributionDate).getTime()
        );
}

/** Desktop four-column grid: Date | Destination | USDv Paid (right) |
 *  Transaction ID (right). Date + USDv Paid are FIXED widths (not `auto`) so the
 *  header row and every data row — which are separate grids — share identical
 *  tracks and line up. Date / USDv Paid stay narrow; Destination + Transaction ID
 *  flex and truncate with an ellipsis when
 *  the screen can't fit the full value. */
const COLUMN_GRID =
    "grid grid-cols-[7rem_minmax(0,1fr)_7rem_minmax(0,1fr)] gap-4 items-center w-full";
/** The `Address` ladder for a transaction signature. The DS default goes
 *  full at 490px, which suits a 44-character address; a signature runs ~88
 *  characters (~930px in the table's own numeric face at `number` size, kept
 *  over the DS mono), so its full rung waits for the room, then steps down
 *  the DS rungs. */
const TX_SIGNATURE_TIERS: readonly AddressTier[] = [
    { minWidth: 940 },
    { minWidth: 215, head: 8, tail: 8, separator: "..." },
    { minWidth: 115, head: 4, tail: 4, separator: ".." },
    { minWidth: 0, head: 2, tail: 4, separator: ".." }
];

/** Mobile three-column grid — Transaction ID dropped, columns tightened to fit a phone. */
const COLUMN_GRID_MOBILE =
    "grid grid-cols-[1.2fr_1fr_1fr] gap-4 items-center w-full";

/**
 * Per-holding transactions table — every reward distribution streamed to this
 * holding, on-chain, across all of its underlying positions. List-items style
 * (matching the Dashboard's Transactions table). Pool + Source are constant
 * here, so the columns trim to Date · Destination · USDv Paid · Transaction ID.
 */
export function PositionTransactionsCard({
    holding,
    showTitle = true
}: PositionTransactionsCardProps): React.ReactElement {
    const { distributions, isPending, error, refetch } =
        useAllClientDistributions();
    // Mobile drops the Transaction ID column and shortens the date (no year) + destination
    // (2…2) so the table fits a phone; desktop keeps the full four columns.
    const isMobile = useIsMobileViewport();
    const columnGrid = isMobile ? COLUMN_GRID_MOBILE : COLUMN_GRID;

    // Resolve a distribution's destination wallet to a saved-destination name
    // (falls back to the address when there's no match).
    const { destinations } = useSavedDestinations();
    const destNameByAddress = useMemo(() => {
        const byAddress = new Map<string, string>();
        for (const d of destinations) {
            if (d.name) byAddress.set(d.address, d.name);
        }
        return byAddress;
    }, [destinations]);

    const rows = useMemo(
        () => holdingDistributions(distributions, holding),
        [distributions, holding]
    );

    return (
        <section aria-label="Transactions" className="flex flex-col gap-4">
            {showTitle && (
                <Text
                    variant="h2"
                    render={<h2 />}
                    className="text-foreground-base px-4 md:px-6"
                >
                    Transactions
                </Text>
            )}

            {error ? (
                <div
                    role="alert"
                    className="px-4 md:px-6 flex items-center gap-4"
                >
                    <Text variant="body" className="text-foreground-muted">
                        Could not load transactions.
                    </Text>
                    <Button
                        variant="transparent"
                        onClick={() => void refetch()}
                    >
                        Retry
                    </Button>
                </div>
            ) : isPending ? (
                <div role="status" className="px-4 md:px-6">
                    <Text variant="body" className="text-foreground-muted">
                        Loading transactions…
                    </Text>
                </div>
            ) : rows.length === 0 ? (
                <div className="px-4 md:px-6">
                    <Text variant="body" className="text-foreground-muted">
                        No distributions yet for this position.
                    </Text>
                </div>
            ) : (
                // Under the tabs the tab bar's rule already tops the table.
                <div className={showTitle ? "border-t border-line-base" : ""}>
                    {/* Column header row — Transaction ID is desktop-only, and
                        reads TxID until the column has room for the words. */}
                    <div
                        className={`${columnGrid} px-4 md:px-6 py-1 md:py-3 border-b border-line-base text-foreground-muted`}
                    >
                        <HeaderCell>Date</HeaderCell>
                        <HeaderCell>Destination</HeaderCell>
                        <HeaderCell align="right">USDv Paid</HeaderCell>
                        {!isMobile && (
                            <HeaderCell align="right">
                                <span className="hidden lg:inline">
                                    Transaction ID
                                </span>
                                <span className="lg:hidden">TxID</span>
                            </HeaderCell>
                        )}
                    </div>

                    <List>
                        {rows.map((row) => (
                            <ListItem
                                key={row.uuid}
                                itemKey={row.uuid}
                                href={solscanTxUrl(row.txSignature)}
                                external
                                padding="px-4 md:px-6 py-1 md:py-3"
                            >
                                <div className={columnGrid}>
                                    <Text
                                        variant="body"
                                        render={<span />}
                                        className="text-foreground-base"
                                    >
                                        {isMobile
                                            ? new Date(
                                                  row.distributionDate
                                              ).toLocaleDateString("en-US", {
                                                  month: "short",
                                                  day: "numeric"
                                              })
                                            : formatLongDate(
                                                  new Date(
                                                      row.distributionDate
                                                  ).getTime()
                                              )}
                                    </Text>
                                    {destNameByAddress.get(
                                        row.destinationWallet
                                    ) ? (
                                        <Text
                                            variant="body"
                                            render={<span />}
                                            className="min-w-0 truncate text-foreground-base"
                                        >
                                            {destNameByAddress.get(
                                                row.destinationWallet
                                            )}
                                        </Text>
                                    ) : (
                                        <Address
                                            mono={false}
                                            value={row.destinationWallet}
                                            className="text-foreground-muted"
                                        />
                                    )}
                                    <Text
                                        variant="number"
                                        render={<span />}
                                        className="text-right text-foreground-base"
                                    >
                                        {formatUsd(row.amountUsdv, {
                                            fractionDigits: 2
                                        })}
                                    </Text>
                                    {!isMobile && (
                                        // The whole row is the Solscan link (see
                                        // ListItem href); this is just the visual
                                        // affordance — not a nested anchor.
                                        <div className="flex min-w-0 items-center justify-end gap-1 text-foreground-base">
                                            <Address
                                                mono={false}
                                                value={row.txSignature}
                                                tiers={TX_SIGNATURE_TIERS}
                                                className="flex-1 text-right underline decoration-line-base underline-offset-2"
                                            />
                                            <ArrowShortUpRightIcon className="size-3.5 shrink-0" />
                                        </div>
                                    )}
                                </div>
                            </ListItem>
                        ))}
                    </List>
                </div>
            )}
        </section>
    );
}

/* ── Primitives ──────────────────────────────────────────── */

function HeaderCell({
    children,
    align = "left"
}: {
    children: React.ReactNode;
    align?: "left" | "right";
}): React.ReactElement {
    return (
        <Text
            variant="label"
            render={<span />}
            className={align === "right" ? "text-right" : ""}
        >
            {children}
        </Text>
    );
}
